import { formatCurrency } from "@/lib/format";
import { InlineError, InlineSpinner, inputClasses, singleLineKeys, useInlineEdit, ViewSurface } from "./shared";

/**
 * An amount, edited in place.
 *
 * The **raw decimal string** is what is edited and what is sent — money is exact, and
 * round-tripping it through a JavaScript number is how a value loses its last koruna. The
 * display goes through `formatCurrency`, like every other amount on screen.
 *
 * Nothing is validated here: `createOpportunitySchema.nip` is the rule, it lives at the API
 * boundary, and a second copy of the regex in the browser is a second place for it to drift.
 * A refused value comes back as a field error and lands under the input.
 */
export function InlineCurrency({
  label,
  value,
  editable,
  disabled,
  onCommit,
}: {
  label: string;
  /** A decimal string, exactly as the API sends it. */
  value: string;
  editable: boolean;
  disabled?: boolean;
  onCommit: (next: string) => Promise<unknown>;
}) {
  const field = useInlineEdit<string>({
    value,
    // Compared numerically: "100000.00" and "100000" are the same amount, and re-typing one
    // as the other is not an edit worth a version bump.
    equals: (a, b) => a.trim() === b.trim() || Number(a) === Number(b),
    onCommit: (next) => onCommit(next.trim()),
  });

  if (!field.editing) {
    return (
      <ViewSurface
        label={label}
        editable={editable}
        disabled={disabled}
        saving={field.saving}
        onActivate={field.begin}
        className="tabular"
      >
        {formatCurrency(value)}
      </ViewSurface>
    );
  }

  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1.5">
        <input
          aria-label={label}
          value={field.draft}
          inputMode="decimal"
          disabled={field.saving}
          onChange={(event) => field.setDraft(event.target.value)}
          onBlur={() => void field.commit(field.draft)}
          onKeyDown={singleLineKeys(field, () => field.draft)}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          className={inputClasses("sm")}
        />
        {field.saving && <InlineSpinner />}
      </div>
      {field.error && <InlineError message={field.error} />}
    </div>
  );
}
